import { clsx } from '@alexis/helpers/clsx';
import { useFlightSearchResults } from '@pages/FlightSearchResult/contexts/FlightSearchResultsContext';
import useFlightSearchFilters from '@pages/FlightSearchResult/hooks/useFlightSearchFilters';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { getIsRtlState } from '@redux/selectors';

import type { FlightSortType } from '@wegoTypes/flights/flightSortType';
import type { SortOrder } from '@wegoTypes/sortOrder';

import styles from './FlightSort.module.scss';
import FlightSortButtonTab from './FlightSortButtonTab/FlightSortButtonTab';
import FlightSortDropdownTab from './FlightSortDropdownTab/FlightSortDropdownTab';

export interface FlightSortProps {
  onSortTypeChanged(sortType: FlightSortType, sortOrder: SortOrder): void;
}

const FlightSort: React.FC<FlightSortProps> = ({ onSortTypeChanged }) => {
  const isRtl = useSelector(getIsRtlState);

  const { selectedLegs } = useFlightSearchFilters();

  const { bestExperienceCheapestFare, cheapestFare, fastestCheapestFare } =
    useFlightSearchResults();

  const flightSortOptions = useMemo(
    () => [
      {
        translationKey: 'recommended',
        sortOrder: 'desc' as SortOrder,
        sortType: 'score' as FlightSortType,
        cheapestFare: bestExperienceCheapestFare,
      },
      {
        translationKey: 'cheapest',
        sortOrder: 'asc' as SortOrder,
        sortType: 'price' as FlightSortType,
        cheapestFare: cheapestFare,
      },
      {
        translationKey: 'fastest',
        sortOrder: 'asc' as SortOrder,
        sortType: 'duration' as FlightSortType,
        cheapestFare: fastestCheapestFare,
      },
    ],
    [bestExperienceCheapestFare, cheapestFare, fastestCheapestFare],
  );

  const disabled = selectedLegs.length > 0;

  return (
    <div className={clsx(styles.container, isRtl && styles.rtl, disabled && styles.disabled)}>
      {/* Recommended, Cheapest and Fastest sorting */}
      {flightSortOptions.map((flightSortOption, index) => {
        return (
          <FlightSortButtonTab
            key={index}
            cheapestFare={flightSortOption.cheapestFare}
            sortOrder={flightSortOption.sortOrder}
            sortType={flightSortOption.sortType}
            translationKey={flightSortOption.translationKey}
            handleOnSortTypeChanged={onSortTypeChanged}
          />
        );
      })}

      {/* Flight Time sorting */}
      <FlightSortDropdownTab handleOnSortTypeChanged={onSortTypeChanged} />
    </div>
  );
};

export default FlightSort;
